/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Stack } from '../../../design-system/layout/Stack';
import { Cluster } from '../../../design-system/layout/Cluster';
import { Select } from '../../../design-system/atoms/Select/Select';
import { Input } from '../../../design-system/atoms/Input/Input';
import { Button } from '../../../design-system/atoms/Button/Button';
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { Table } from '../../../design-system/molecules/Table/Table';
import { ChartLegend } from '../../../design-system/molecules/ChartLegend/ChartLegend';
import { PopulationChart } from '../../organisms/PopulationChart/PopulationChart';
import { theme } from '../../../design-system/theme/theme';

export interface FacetItem {
  label: string;
  count: number;
  /** 스택 세그먼트 색과 맞추기 위한 색 점 (선택). */
  color?: string;
}

export interface Facet {
  title: string;
  items: FacetItem[];
}

export interface PopulationScreenProps {
  moduleOptions: string[];
  module: string;
  onChangeModule: (m: string) => void;
  tableOptions: { label: string; value: string }[];
  table: string;
  onChangeTable: (t: string) => void;
  /** 현재 테이블에서 그룹 기준으로 쓸 수 있는 컬럼들. */
  dimensionOptions: string[];
  /** 그룹 기준 3단계. 각 단계는 선택(빈 문자열 = 없음). */
  levels: string[];
  onChangeLevel: (index: number, value: string) => void;
  /** 기간 필터 지원 여부(테이블 기본 날짜 컬럼 존재 시). false면 기간 UI 미표시. */
  rangeSupported: boolean;
  /** 기간 시작/끝(yyyy-MM-dd, 빈 문자열 = 없음). */
  from: string;
  to: string;
  onChangeFrom: (v: string) => void;
  onChangeTo: (v: string) => void;
  onClearRange: () => void;
  /** 차트 패널 제목. */
  chartTitle: string;
  /** 날짜 기준 스택 막대. */
  chartData: { label: string; segments: { key: string; value: number }[] }[];
  /** 스택 세그먼트 색 조회. */
  colorOf: (key: string) => string;
  /** 차트 범례 (스택 시에만). 없으면 미표시. */
  legend: { color: string; label: string }[];
  /** 우측 필터 패널: 비-날짜 dimension별 값 카운트. */
  facets: Facet[];
  /** 그룹 결과 표 머리글 (그룹 컬럼들 + '건수'). */
  groupColumns: string[];
  groupRows: (string | number)[][];
  totalCount: number;
  /** 메인 집계 요청 진행 중. */
  loading: boolean;
  /** 메인 집계 요청 실패 메시지 (없으면 null). */
  error: string | null;
}

const separatorStyle = css`
  color: ${theme.color.textMuted};
`;

const levelLabelStyle = css`
  margin-left: 10px;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};
`;

const noteStyle = css`
  margin: 0;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};
`;

const errorStyle = css`
  margin: 0;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.danger};
`;

/* 필터 패널: 차트 아래 가로로 나열, 좁으면 줄바꿈. */
const facetColStyle = css`
  display: flex;
  gap: ${theme.space.md}px;
  flex-wrap: wrap;
`;

const facetPanelStyle = css`
  width: 190px;
`;

const facetListStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs}px;
  max-height: 240px;
  overflow-y: auto;
`;

const facetRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.sm}px;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.text};
`;

const facetNameStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.space.sm}px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const dotStyle = (color: string) => css`
  width: 8px;
  height: 8px;
  border-radius: ${theme.radius.pill}px;
  background: ${color};
  flex-shrink: 0;
`;

const facetCountStyle = css`
  color: ${theme.color.textMuted};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const countCellStyle = css`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const emptyRowStyle = css`
  text-align: center;
  color: ${theme.color.textMuted};
  padding: ${theme.space.md}px;
`;

const toOptions = (list: string[]) => list.map((o) => ({ label: o, value: o }));

/** 집계 뷰어 = groupby로 날짜별 전체 population을 보는 뷰어. 그룹 기준 최대 3단계. */
export function PopulationScreen(props: PopulationScreenProps) {
  const {
    moduleOptions, module, onChangeModule,
    tableOptions, table, onChangeTable,
    dimensionOptions, levels, onChangeLevel,
    rangeSupported, from, to, onChangeFrom, onChangeTo, onClearRange,
    chartTitle, chartData, colorOf, legend, facets,
    groupColumns, groupRows, totalCount, loading, error,
  } = props;

  // 각 단계 옵션: 앞 단계에서 이미 고른 컬럼은 제외. 모든 단계 "(없음)" 허용.
  const levelOptions = (index: number) => {
    const used = levels.slice(0, index).filter(Boolean);
    const avail = dimensionOptions.filter((d) => !used.includes(d));

    let default_label;
    default_label = (index === 0)? "기본날짜" :"(없음)";
    // if (index === 0){
    //   default_label = "(기본날짜)"
    // }
    return [{ label: default_label, value: '' }, ...toOptions(avail)];
  };

  return (
    <Stack gap="md">
      {/* 데이터 소스: 모듈 / 테이블 선택 */}
      <Cluster gap="sm">
        <Select options={toOptions(moduleOptions)} value={module} onChange={onChangeModule} />
        <span css={separatorStyle}>/</span>
        <Select options={tableOptions} value={table} onChange={onChangeTable} />
      </Cluster>

      {/* 그룹 기준 3단계(2·3단계 선택) + 기간 필터를 한 줄에. 좁으면 줄바꿈. */}
      <Cluster gap="sm">
        <span css={levelLabelStyle}>그룹 기준</span>
        <Select options={levelOptions(0)} value={levels[0]} onChange={(v) => onChangeLevel(0, v)} />
        <Select options={levelOptions(1)} value={levels[1]} onChange={(v) => onChangeLevel(1, v)} />

        {/* 기간 필터 (테이블 기본 날짜 컬럼 기준). 미지원 테이블에선 숨김. */}
        {rangeSupported && (
          <>
            <span css={levelLabelStyle}>기간</span>
            <Input type="date" value={from} onChange={onChangeFrom} />
            <span css={separatorStyle}>~</span>
            <Input type="date" value={to} onChange={onChangeTo} />
            {(from || to) && (
              <Button variant="text" tone="neutral" onClick={onClearRange}>
                초기화
              </Button>
            )}
          </>
        )}

        {loading && <span css={levelLabelStyle}>불러오는 중…</span>}
      </Cluster>

      {error && <p css={errorStyle}>집계 조회 실패: {error}</p>}

      {/* 스택 막대 차트: 한 줄 전체 차지 */}
      <PanelCard
        title={chartTitle}
        headerRight={legend.length > 0 ? <ChartLegend items={legend} /> : undefined}
      >
        <PopulationChart data={chartData} colorOf={colorOf} />
      </PanelCard>

      {/* dimension별 필터 패널: 차트 아래 별도 행 */}
      {facets.length > 0 && (
        <div css={facetColStyle}>
          {facets.map((facet) => (
            <div key={facet.title} css={facetPanelStyle}>
              <PanelCard title={facet.title}>
                <div css={facetListStyle}>
                  {facet.items.map((item) => (
                    <div key={item.label} css={facetRowStyle}>
                      <span css={facetNameStyle}>
                        {item.color && <span css={dotStyle(item.color)} />}
                        {item.label}
                      </span>
                      <span css={facetCountStyle}>({item.count.toLocaleString()})</span>
                    </div>
                  ))}
                </div>
              </PanelCard>
            </div>
          ))}
        </div>
      )}

      {/* groupby 결과 표 */}
      <PanelCard title="그룹 집계" headerRight={<span css={noteStyle}>총 {totalCount.toLocaleString()}건</span>}>
        <Table headers={[...groupColumns.slice(0, -1), <span css={countCellStyle}>건수</span>]}>
          {groupRows.length === 0 ? (
            <tr>
              <td css={emptyRowStyle} colSpan={groupColumns.length}>
                데이터가 없습니다.
              </td>
            </tr>
          ) : (
            groupRows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} css={j === row.length - 1 ? countCellStyle : undefined}>
                    {typeof cell === 'number' ? cell.toLocaleString() : cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </Table>
      </PanelCard>

      <p css={noteStyle}>※ 실시간 groupby 집계 API(/data/{'{'}schema{'}'}/{'{'}table{'}'}/groupby) 결과입니다.</p>
    </Stack>
  );
}
